import { reactive } from 'vue';

export type DateFormat = 'iso' | 'dmy' | 'mdy' | 'long';
export type TimeFormat = '24h' | '12h';
export type WeekStart = 'monday' | 'sunday';

/** Display-format config, driven by the user's per-device preferences
 *  (see useAppStore). This is a Vue `reactive` object, so any component that
 *  calls formatDate/formatDateTime in its template automatically re-renders
 *  when a preference changes — no call-site changes needed across the app.
 *  `locale` only affects month names in the `long` date style. */
export const dateConfig = reactive<{
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  weekStart: WeekStart;
  locale: string;
}>({
  dateFormat: 'dmy',
  timeFormat: '24h',
  weekStart: 'monday',
  locale: 'sq-AL',
});

function datePart(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  switch (dateConfig.dateFormat) {
    case 'iso':
      return `${y}-${m}-${day}`;
    case 'mdy':
      return `${m}/${day}/${y}`;
    case 'long':
      return new Intl.DateTimeFormat(dateConfig.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(d);
    case 'dmy':
    default:
      return `${day}/${m}/${y}`;
  }
}

function timePart(d: Date): string {
  return new Intl.DateTimeFormat(dateConfig.locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: dateConfig.timeFormat === '12h',
  }).format(d);
}

/** ISO date string → date for display, per the user's date-format preference.
 *  `localeOverride` forces the legacy fixed style in a specific locale — used by
 *  deterministic outputs (receipts, PDFs) that must NOT follow user prefs. */
export function formatDate(iso: string | null | undefined, localeOverride?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (localeOverride) {
    return new Intl.DateTimeFormat(localeOverride, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(d);
  }
  return datePart(d);
}

/** ISO date string → date + time for display, per the user's preferences.
 *  `localeOverride` forces the legacy fixed style (see formatDate). */
export function formatDateTime(iso: string | null | undefined, localeOverride?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (localeOverride) {
    return new Intl.DateTimeFormat(localeOverride, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }
  return `${datePart(d)} ${timePart(d)}`;
}

/** YYYY-MM-DD (server-side filter format). */
export function toApiDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function startOfMonth(d: Date = new Date()): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date = new Date()): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

/** Start of the week, honouring the user's first-day-of-week preference
 *  (Monday by default; timesheets typically span a Mon–Sun range). */
export function startOfWeek(d: Date = new Date()): Date {
  const clone = new Date(d);
  const dow = clone.getDay(); // Sun = 0 … Sat = 6
  const offset = dateConfig.weekStart === 'sunday' ? dow : (dow + 6) % 7;
  clone.setDate(clone.getDate() - offset);
  clone.setHours(0, 0, 0, 0);
  return clone;
}

export function endOfWeek(d: Date = new Date()): Date {
  const start = startOfWeek(d);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end;
}
